<template>
  <div id="licensing-catalog" class="licensing-page">
    <!-- Header Section -->
    <div class="vx-row mb-6">
      <div class="vx-col w-full">
        <vx-card>
          <div class="text-center">
            <h1 class="mb-4">TCVelik {{ $t('TCVelikLicensing') }}</h1>
            <p class="text-lg text-gray-600 mb-4">
              {{ $t('ChoosePerfectPlan') }}
            </p>
            <div class="help-section">
                {{ $t('LicensingHelpText') }} <a href="https://tcvelik.com?utm_source=license_screen&utm_medium=app&utm_campaign=license_selection" target="_blank" class="text-primary underline">{{ $t('Website') }}</a> {{ $t('Or') }} 
                <a href="https://outlook.office.com/bookwithme/user/83ac2dd7d76046e3a2f5e194c81e5011@eveliko.com/meetingtype/s3RCe-lgsUCRR4Xx0lgC3A2?anonymous&ep=mlink" target="_blank" class="text-primary underline">{{ $t('BookMeeting') }}</a> {{ $t('WithUsDirectly') }}
             
            </div>
          </div>
        </vx-card>
      </div>
    </div>

    <!-- Consolidated Licenses Table -->
    <div class="vx-row mb-6" v-if="(currentLicense && currentLicense.Edition && currentLicense.Edition.toLowerCase() !== 'disabled') || loadingAvailableLicenses || (!loadingAvailableLicenses && mounted)">
      <div class="vx-col w-full">
        <vx-card :title="$t('YourLicenses')" class="license-status-card">
        <div class="license-table-header">
            <div class="license-row">
              <div class="license-holder">{{ $t('LicenseHolder') }}</div>
              <div class="license-edition">{{ $t('Edition') }}</div>
              <div class="license-renewal">{{ $t('RenewsAt') }}</div>
              <div class="license-domain">{{ $t('Domain') }}</div>
              <div class="license-action">{{ $t('Action') }}</div>
            </div>
          </div>
          
          <!-- Current License Row -->
          <div 
            v-if="currentLicense && currentLicense.Edition && currentLicense.Edition.toLowerCase() !== 'disabled'"
            class="license-row current-license">
            <div class="license-holder">
              <span class="holder-text">{{ currentLicense.Email }}</span>
            </div>
            <div class="license-edition">
              <span class="edition-text">{{ currentLicense.Edition }}</span>
            </div>
            <div class="license-subscription">
              <span class="subscription-text font-mono text-xs">{{ currentLicense.SubscriptionId || 'N/A' }}</span>
            </div>
            <div class="license-renewal">
              <span class="renewal-text">{{ formatRenewalDate(currentLicense.ExpiresAt) }}</span>
            </div>
            <div class="license-domain">
              <span class="domain-text">{{ currentLicense.Hostname }}</span>
            </div>
            <div class="license-action">
              <i class="fas fa-check-circle text-success text-2xl"></i>
            </div>
          </div>
          
          <!-- Loading State -->
          <div v-if="loadingAvailableLicenses" class="license-row loading-row">
            <div class="loading-message">
              <vs-progress indeterminate color="primary"></vs-progress>
              <span class="ml-2">Loading available licenses...</span>
            </div>
          </div>
          
          <!-- Available Licenses Rows -->
          <div 
            v-else-if="availableLicenses.length > 0"
            v-for="license in availableLicenses" 
            :key="license.SubscriptionId"
            class="license-row">
            <div class="license-holder">
              <span class="holder-text">{{ license.Email || $t('NA') }}</span>
            </div>
            <div class="license-edition">
              <span class="edition-text">{{ license.Edition || $t('NA') }}</span>
            </div>
            <div class="license-subscription">
              <span class="subscription-text font-mono text-xs">{{ license.SubscriptionId }}</span>
            </div>
            <div class="license-renewal">
              <span class="renewal-text">{{ formatRenewalDate(license.ExpiresAt) }}</span>
            </div>
            <div class="license-domain">
              <span class="domain-text">{{ license.Hostname || $t('AvailableForUse') }}</span>
            </div>
            <div class="license-action">
              <vs-button 
                color="primary" 
                size="small"
                @click="switchToLicense(license.SubscriptionId)">
                {{ $t('UseThisLicense') }}
              </vs-button>
            </div>
          </div>
          
          <!-- No Licenses Message -->
          <div v-else class="license-row no-licenses-row">
            <div class="no-licenses-message">
              <vs-alert color="warning" icon="info">
                <h4 class="mb-2">No Licenses Available</h4>
                <p>No licenses available under your account with us. Please contact the license holder, buy a license, or email <a href="mailto:sales@tcvelik.com">sales@tcvelik.com</a> in case of issues with licensing.</p>
              </vs-alert>
            </div>
          </div>
        </vx-card>
      </div>
    </div>

    <!-- License Information Message -->
    <div class="vx-row mb-6" v-if="(currentLicense && currentLicense.Edition && currentLicense.Edition.toLowerCase() !== 'disabled') || loadingAvailableLicenses || (!loadingAvailableLicenses && mounted)">
      <div class="vx-col w-full">
        <vx-card class="license-info-card">
          <div class="license-info-content">
            <i class="fas fa-info-circle text-primary mr-2"></i>
            <span>If you just bought a license - it may take a few seconds to reflect. Please use the refresh button, if you have any issues - contact us at <a href="tel:+359883463241" class="text-primary">+359883463241</a></span>
          </div>
        </vx-card>
      </div>
    </div>

    <div class="vx-row mb-6">
      <div class="vx-col w-full">
        <vx-card>
          <div class="text-center">
            <h1 class="mb-4">{{ $t('PurchaseAdditionalLicenses') }}</h1>
            <p class="text-lg text-gray-600 mb-4">
              {{ $t('ChoosePerfectPlan') }}
            </p>
            <div class="help-section">
                
            </div>
          </div>
        </vx-card>
      </div>
    </div>

    <!-- Products Grid -->
    <div class="vx-row" v-if="products.length > 0">
      <div 
        v-for="product in products" 
        :key="product.Id" 
        class="vx-col w-full md:w-1/2 lg:w-1/3 xl:w-1/3 mb-base">
        <vx-card class="product-card" :class="{ 'auto-activation-product': isAutoActivationProduct(product) }">
          <!-- Product Image as Icon -->
          <div class="product-icon-container mb-4">
            <img 
              v-if="product.Images && product.Images.length > 0"
              :src="product.Images[0]" 
              :alt="product.Name"
              class="product-icon">
            <div v-else class="product-icon-placeholder">
              <i :class="getProductPlaceholderIcon(product)" class="text-4xl text-gray-400"></i>
            </div>
          </div>
          
          <!-- Product Info -->
          <div class="product-info">
            <h3 class="product-name mb-2">{{ product.Name }}</h3>
            <p class="product-description text-gray-600 mb-4">
              {{ product.Description || $t('NoDescriptionAvailable') }}
            </p>
            
            <!-- Pricing Display -->
            <div class="pricing-display mb-4">
              <div 
                v-for="price in product.Prices" 
                :key="price.Id"
                class="price-display">
                <div class="text-2xl font-bold text-primary mb-1">
                  {{ getDisplayPrice(price, product) }}
                </div>
                <div class="text-sm text-gray-500">
                  {{ getPriceDescription(price, product) }}
                  <div v-if="isAutoActivationProduct(product)" class="download-link mt-2">
                    <p class="text-sm text-gray-600">
                      {{ $t('YouAndYourTeamCanDownload') }} 
                      <a href="https://tcvelik.com/downloads/?utm_source=tcvelik&utm_medium=license_page&utm_campaign=personal_edition" 
                         target="_blank" 
                         class="text-primary hover:underline">
                        {{ $t('Here') }}
                      </a>
                    </p>
                  </div>
                </div>
              </div>
            </div>
            
            <!-- Action Button -->
            <div class="action-button-container">
              <vs-button 
                color="primary" 
                size="large"
                class="w-full"
                :disabled="loading || isPersonalEditionDisabled(product)"
                :loading="loading"
                @click="handleProductAction(product)">
                <i :class="getButtonIcon(product)" class="mr-2"></i>
                {{ getButtonText(product) }}
              </vs-button>
            </div>
          </div>
        </vx-card>
      </div>
    </div>

    <!-- Loading State -->
    <div v-else-if="loading" class="vx-row">
      <div class="vx-col w-full text-center">
        <vs-loading type="border" size="large"></vs-loading>
        <p class="mt-4">{{ $t('LoadingLicensingOptions') }}</p>
      </div>
    </div>

    <!-- No Products State -->
    <div v-else class="vx-row">
      <div class="vx-col w-full text-center">
        <vx-card>
          <div class="py-8">
            <i class="fas fa-exclamation-triangle text-4xl text-warning mb-4"></i>
            <h3 class="mb-2">{{ $t('NoLicensingOptionsAvailable') }}</h3>
            <p class="text-gray-600">
              {{ $t('NoLicensingOptionsText') }}
            </p>
          </div>
        </vx-card>
      </div>
    </div>
    
    <!-- Email Input Popup for Auto-Activation -->
    <vs-popup 
      :title="$t('ActivateLicense')" 
      :active.sync="showEmailPopup" 
      class="email-popup">
      <div class="email-popup-content">
        <p class="mb-4">{{ $t('EnterEmailToActivate') }}</p>
        
        <vs-input
          v-model="emailInput"
          :placeholder="$t('EmailPlaceholder')"
          type="email"
          class="w-full mb-4"
          :label="$t('EmailAddress')">
        </vs-input>
        
        <div class="flex justify-end space-x-2">
          <vs-button 
            color="danger" 
            type="flat"
            @click="showEmailPopup = false">
            {{ $t('Cancel') }}
          </vs-button>
          <vs-button 
            color="primary"
            :disabled="!emailInput || !isValidEmail(emailInput)"
            @click="activateWithEmail">
            {{ $t('Activate') }}
          </vs-button>
        </div>
      </div>
    </vs-popup>

    <!-- Success Modal -->
    <vs-popup 
      title="Payment Successful!" 
      :active.sync="showSuccessModal" 
      class="success-modal"
      @close="closeSuccessModal">
      
      <div class="success-modal-content">
        <div class="success-animation">
          <div class="checkmark-circle">
            <div class="checkmark"></div>
          </div>
        </div>
        
        <div class="success-message">
          <h3 class="success-title">Success!</h3>
          <p class="success-text">
            Your license should now be available in "Available licenses". If you plan to use it for this instance of TCVelik - click Use this license.
          </p>
        </div>
        
        <div class="success-actions">
          <vs-button 
            color="primary" 
            @click="closeSuccessModal">
            Got it!
          </vs-button>
        </div>
      </div>
    </vs-popup>
  </div>
</template>

<script>
import TCVelikClient from '@/TCVelikClient/TCVelikClient.js';

export default {
  name: 'LicensingCatalog',
  data() {
    return {
      products: [],
      selectedPrice: null,
      selectedProduct: null,
      loading: false,
      showHelpSection: true,
      showEmailPopup: false,
      emailInput: '',
      currentLicense: null,
      loadingLicense: false,
      availableLicenses: [],
      loadingAvailableLicenses: false,
      switchingLicense: null,
      mounted: false,
      showSuccessModal: false
    };
  },
  mounted() {
    this.mounted = true;
    this.loadProducts();
    this.refreshLicenseCache();
    this.loadAvailableLicenses();
    
    // Check for success parameter from payment callback
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('success') === 'true') {
      this.showSuccessModal = true;
      // Clean up the URL
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  },
  methods: {
    async loadProducts() {
      this.loading = true;
      try {
        const response = await TCVelikClient.GetRelative('/api/licensing/products');
        this.products = response.data;
      } catch (error) {
        console.error('Error loading products:', error);
        this.$vs.notify({
          title: this.$t('Error'),
          text: this.$t('FailedToLoadProducts'),
          color: 'danger'
        });
      } finally {
        this.loading = false;
      }
    },
    
    handleProductAction(product) {
      // Check if this product supports auto-activation
      if (this.isAutoActivationProduct(product)) {
        this.showEmailModal(product);
      } else {
        // For regular products, use the first price
        if (product.Prices && product.Prices.length > 0) {
          this.selectedPrice = product.Prices[0].Id;
          this.proceedWithPurchase();
        } else {
          this.$vs.notify({
            title: this.$t('NoPricingAvailable'),
            text: this.$t('NoPricingOptionsText'),
            color: 'warning'
          });
        }
      }
    },

    purchaseProduct(product) {
      if (!this.selectedPrice) {
        this.$vs.notify({
          title: this.$t('SelectionRequired'),
          text: this.$t('PleaseSelectPricing'),
          color: 'warning'
        });
        return;
      }
      
      // Proceed directly with purchase - Stripe will collect email
      this.proceedWithPurchase();
    },
    
    async proceedWithPurchase() {
      this.loading = true;
      
      try {
        const response = await TCVelikClient.PostRelative('/api/licensing/create-payment-link', {
          PriceId: this.selectedPrice
        });
        
        if (response.data.Success) {
          // Redirect to Stripe PaymentLink
          window.location.href = response.data.PaymentLinkUrl;
        } else {
          throw new Error('Failed to create payment link');
        }
      } catch (error) {
        console.error('Error creating payment link:', error);
        this.$vs.notify({
          title: this.$t('Error'),
          text: this.$t('FailedToCreatePaymentLink'),
          color: 'danger'
        });
      } finally {
        this.loading = false;
      }
    },
    
    isPersonalEditionDisabled(product) {
      if (!this.isAutoActivationProduct(product)) {
        return false;
      }
      
      // Check if current hostname is personal.tcvelik.com
      const currentHostname = window.location.hostname;
      return currentHostname !== 'personal.tcvelik.com';
    },

    getButtonText(product) {
      if (this.loading) return this.$t('Processing');
      
      if (this.isAutoActivationProduct(product)) {
        if (this.isPersonalEditionDisabled(product)) {
          return this.$t('OnlyAvailableOnPersonal');
        }
        return this.$t('Activate');
      }
      return this.$t('PurchaseNow');
    },

    getButtonIcon(product) {
      if (this.isAutoActivationProduct(product)) {
        return 'fas fa-check';
      }
      return 'fas fa-shopping-cart';
    },


    getDisplayPrice(price, product) {
      if (this.isAutoActivationProduct(product)) {
        return this.$t('FreeForever');
      }
      if (!price.UnitAmount) {
        return this.$t('ContactSales');
      }
      return this.formatPrice(price.UnitAmount, price.Currency);
    },

    getPriceDescription(price, product) {
      if (this.isAutoActivationProduct(product)) {
        return this.$t('NoCreditCardRequired');
      }
      
      if (price.Recurring) {
        return `${this.$t('Per')} ${price.Recurring.Interval}`;
      }
      return this.$t('OneTimePayment');
    },

    getProductPlaceholderIcon(product) {
      if (this.isAutoActivationProduct(product)) {
        return 'fas fa-gift';
      }
      return 'fas fa-box';
    },

    isAutoActivationProduct(product) {
      // Check if product has ApiOnlyActivation metadata set to true (case-insensitive)
      return product.Metadata && 
             product.Metadata.ApiOnlyActivation && 
             product.Metadata.ApiOnlyActivation.toLowerCase() === 'true';
    },

    showEmailModal(product) {
      this.selectedProduct = product;
      this.emailInput = '';
      this.showEmailPopup = true;
    },

    activateWithEmail() {
      if (this.emailInput && this.isValidEmail(this.emailInput)) {
        this.proceedWithAutoActivation(this.emailInput);
        this.showEmailPopup = false;
      } else {
        this.$vs.notify({
          title: this.$t('InvalidEmail'),
          text: this.$t('PleaseEnterValidEmail'),
          color: 'warning'
        });
      }
    },

    async proceedWithAutoActivation(email) {
      this.loading = true;
      
      try {
        // Use the first price from the selected product
        const priceId = this.selectedProduct && this.selectedProduct.Prices && this.selectedProduct.Prices.length > 0 
          ? this.selectedProduct.Prices[0].Id 
          : null;
          
        if (!priceId) {
          throw new Error('No price available for this product');
        }
        
        const response = await TCVelikClient.PostRelative('/api/licensing/auto-activate', {
          Email: email,
          PriceId: priceId
        });
        
        if (response.data.Success) {
          this.$vs.notify({
            title: this.$t('LicenseActivated'),
            text: this.$t('LicenseActivatedSuccess'),
            color: 'success'
          });
          
          // Refresh license status
          await this.loadLicenseStatus();
          
          // Redirect to dashboard after a short delay
          setTimeout(() => {
            this.$router.push('/pages/home');
          }, 2000);
        } else {
          throw new Error(response.data.Message || this.$t('FailedToActivateLicense'));
        }
      } catch (error) {
        console.error('Error auto-activating license:', error);
        this.$vs.notify({
          title: this.$t('ActivationFailed'),
          text: error.message || this.$t('FailedToActivateLicense'),
          color: 'danger'
        });
      } finally {
        this.loading = false;
      }
    },

    isValidEmail(email) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      return emailRegex.test(email);
    },
    
    formatPrice(amount, currency) {
      if (!amount) return this.$t('ContactSales');
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: currency.toUpperCase()
      }).format(amount / 100);
    },
    
    formatDate(dateString) {
      if (!dateString) return this.$t('NA');
      return new Date(dateString).toLocaleDateString();
    },

    async refreshLicenseCache() {
      this.loadingLicense = true;
      try {
        // Refresh the license cache to get the most up-to-date information
        const response = await TCVelikClient.PostRelative('/api/licensing/refresh-cache');
        const status = response.data;
        
        if (status.IsValid) {
          this.currentLicense = {
            Email: status.LicenseData?.Email,
            Edition: status.LicenseData?.Edition,
            SubscriptionId: status.LicenseData?.SubscriptionId,
            ExpiresAt: status.LicenseData?.ExpiresAt,
            Hostname: status.LicenseData?.Hostname,
            ValidatedAt: status.LicenseData?.ValidatedAt,
            DaysUntilExpiry: this.calculateDaysUntilExpiry(status.LicenseData?.ExpiresAt)
          };
        } else {
          this.currentLicense = null;
        }
      } catch (error) {
        console.error('Error refreshing license cache:', error);
        // Fallback to regular status check
        await this.loadLicenseStatus();
      } finally {
        this.loadingLicense = false;
      }
    },

    async loadAvailableLicenses() {
      this.loadingAvailableLicenses = true;
      try {
        const response = await TCVelikClient.GetRelative('/api/licensing/alternative-licenses');
        this.availableLicenses = response.data.AlternativeLicenses || [];
      } catch (error) {
        console.error('Error loading available licenses:', error);
        this.availableLicenses = [];
      } finally {
        this.loadingAvailableLicenses = false;
      }
    },

    async refreshAllLicenses() {
      // Refresh both current license and available licenses
      await Promise.all([
        this.refreshLicenseCache(),
        this.loadAvailableLicenses()
      ]);
      
      this.$vs.notify({
        title: 'Licenses Refreshed',
        text: 'License information has been updated.',
        color: 'success'
      });
    },

    closeSuccessModal() {
      this.showSuccessModal = false;
    },

    async switchToLicense(subscriptionId) {
      console.log('Switching to license:', subscriptionId);
      console.log('Available licenses:', this.availableLicenses);
      
      // Find the license details for confirmation
      const license = this.availableLicenses.find(l => l.SubscriptionId === subscriptionId);
      console.log('Found license:', license);
      
      // Show confirmation dialog
      const confirmed = confirm(this.$t('SwitchLicenseConfirmation', { product: license?.ProductName || 'selected' }));
      
      if (!confirmed) {
        return;
      }
      
      this.switchingLicense = subscriptionId;
      
      try {
        console.log('Making API call to switch license...');
        const response = await TCVelikClient.PostRelative('/api/licensing/switch-license', {
          SubscriptionId: subscriptionId
        });
        
        console.log('API response:', response.data);
        
        if (response.data.Success) {
          this.$vs.notify({
            title: this.$t('LicenseSwitched'),
            text: this.$t('LicenseSwitchedSuccess'),
            color: 'success'
          });
          
          // Refresh license cache and available licenses
          await this.refreshLicenseCache();
          await this.loadAvailableLicenses();
          
          // Refresh the page to apply new license
          setTimeout(() => {
            window.location.reload();
          }, 1000);
        } else {
          throw new Error(response.data.Error || this.$t('FailedToSwitchLicense'));
        }
      } catch (error) {
        console.error('Error switching license:', error);
        this.$vs.notify({
          title: this.$t('SwitchFailed'),
          text: error.message || this.$t('FailedToSwitchLicense'),
          color: 'danger'
        });
      } finally {
        this.switchingLicense = null;
      }
    },

    calculateDaysUntilExpiry(expiryDate) {
      if (!expiryDate) return null;
      
      const date = new Date(expiryDate);
      const now = new Date();
      const diffTime = date - now;
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      
      return diffDays;
    },

    formatRenewalDate(renewalDate) {
      if (!renewalDate) return this.$t('NA');
      
      const date = new Date(renewalDate);
      const now = new Date();
      const diffTime = date - now;
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      
      if (diffDays < 0) {
        return this.$t('Renewed');
      } else if (diffDays === 0) {
        return this.$t('RenewsToday');
      } else if (diffDays === 1) {
        return this.$t('RenewsTomorrow');
      } else if (diffDays <= 7) {
        return this.$t('RenewsInDays', { days: diffDays });
      } else {
        return date.toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'short',
          day: 'numeric'
        });
      }
    },

    formatExpiryDate(expiryDate) {
      if (!expiryDate) return this.$t('NA');
      
      const date = new Date(expiryDate);
      const now = new Date();
      const diffTime = date - now;
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      
      if (diffDays < 0) {
        return this.$t('Expired');
      } else if (diffDays === 0) {
        return this.$t('ExpiresToday');
      } else if (diffDays === 1) {
        return this.$t('ExpiresTomorrow');
      } else if (diffDays <= 7) {
        return this.$t('ExpiresInDays', { days: diffDays });
      } else {
        return date.toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'short',
          day: 'numeric'
        });
      }
    },

    async loadLicenseStatus() {
      this.loadingLicense = true;
      try {
        const response = await TCVelikClient.GetRelative('/api/licensing/status');
        const status = response.data;
        
        if (status.IsValid) {
          this.currentLicense = {
            Email: status.Email,
            Edition: status.Edition,
            SubscriptionId: status.SubscriptionId,
            ExpiresAt: status.ExpiresAt,
            Hostname: status.Hostname,
            ValidatedAt: status.ValidatedAt,
            DaysUntilExpiry: status.DaysUntilExpiry
          };
        } else {
          this.currentLicense = null;
        }
      } catch (error) {
        console.error('Error loading license status:', error);
        this.currentLicense = null;
      } finally {
        this.loadingLicense = false;
      }
    }
  }
};
</script>

<style scoped>
/* Import Outfit font */
@import url('https://fonts.googleapis.com/css?family=Outfit:300,400,500,600,700');

.licensing-page {
  padding: 2rem;
  max-width: 1200px;
  margin: 0 auto;
  font-family: 'Outfit', sans-serif;
}

.product-card {
  height: 100%;
  min-height: 400px; /* Ensure minimum height for consistency */
  transition: transform 0.2s ease-in-out;
  display: flex;
  flex-direction: column;
  padding: 1.5rem;
  position: relative;
}

.product-card:hover {
  transform: translateY(-2px);
}

.product-icon-container {
  text-align: center;
  margin-bottom: 1rem;
}

.product-icon {
  width: 80px;
  height: 80px;
  object-fit: contain;
  border-radius: 8px;
}

.product-icon-placeholder {
  width: 80px;
  height: 80px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #f8f9fa;
  border-radius: 8px;
  margin: 0 auto;
}

.product-info {
  flex: 1;
  display: flex;
  flex-direction: column;
  padding-bottom: 80px; /* Space for absolute positioned button */
}

.product-name {
  font-size: 1.25rem;
  font-weight: 600;
  font-family: 'Outfit', sans-serif;
  color: #2d3748;
  margin-bottom: 0.5rem;
}

.product-description {
  font-size: 0.875rem;
  font-weight: 400;
  font-family: 'Outfit', sans-serif;
  line-height: 1.5;
  color: #6b7280;
  margin-bottom: 1rem;
  flex: 1;
}

.pricing-display {
  margin-bottom: 1rem;
}

.price-display {
  margin-bottom: 0.5rem;
}

.price-display .text-2xl {
  font-family: 'Outfit', sans-serif;
  font-weight: 700;
}

.price-display .text-sm {
  font-family: 'Outfit', sans-serif;
  font-weight: 400;
}

.action-button-container {
  position: absolute;
  bottom: 20px;
  left: 1.5rem;
  right: 1.5rem;
  width: calc(100% - 3rem);
}

.action-button-container .vs-button {
  font-family: 'Outfit', sans-serif;
  font-weight: 600;
  font-size: 0.95rem;
}

/* Auto-activation product styling */
.auto-activation-product {
  border: 2px solid #10b981 !important;
  background: linear-gradient(135deg, #f0fdf4 0%, #ffffff 100%) !important;
}

.auto-activation-product .product-name {
  color: #059669 !important;
}

.auto-activation-product .price-display .text-2xl {
  color: #059669 !important;
}

.auto-activation-product .action-button-container .vs-button {
  background: #10b981 !important;
  border-color: #10b981 !important;
}

/* Download link styling */
.download-link {
  font-family: 'Outfit', sans-serif;
}

.download-link a {
  color: #3b82f6;
  text-decoration: underline;
  transition: color 0.2s ease;
}

.download-link a:hover {
  color: #1d4ed8;
}

/* Disabled button styling for personal edition */
.action-button-container .vs-button:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.help-section {
  margin-top: 1rem;
}

.help-section a {
  color: inherit;
  text-decoration: underline;
}

.help-section a:hover {
  opacity: 0.8;
}

/* License Status Card Styling */
.license-status-card {

  border-radius: 12px;
}

.license-status-header {
  padding-bottom: 1rem;
  border-bottom: 1px solid #e0f2fe;
}

.license-status-badge {
  display: flex;
  align-items: center;
}

.license-details {
  background: rgba(255, 255, 255, 0.7);
  border-radius: 8px;
  padding: 1rem;
}

.license-detail-item {
  margin-bottom: 0.5rem;
}

.license-detail-item label {
  display: block;
  margin-bottom: 0.25rem;
  font-family: 'Outfit', sans-serif;
  font-weight: 600;
}

.license-detail-item p {
  margin: 0;
  font-family: 'Outfit', sans-serif;
  font-weight: 500;
  color: #1f2937;
}

/* Available Licenses Styles */
.available-licenses-card {
  background: linear-gradient(135deg, #fef3c7 0%, #fde68a 100%);
  border: 1px solid #f59e0b;
  border-radius: 12px;
  box-shadow: 0 4px 6px rgba(245, 158, 11, 0.1);
}

.available-licenses-header {
  padding-bottom: 1rem;
  border-bottom: 1px solid #fde68a;
}

.available-licenses-table {
  background: rgba(255, 255, 255, 0.8);
  border-radius: 8px;
  overflow: hidden;
  border: 1px solid #e5e7eb;
}

.license-table-header {
  background: #f9fafb;
  border-bottom: 1px solid #e5e7eb;
}

.license-table-body {
  background: white;
}

.license-row {
  display: grid;
  grid-template-columns: 2fr 1fr 2fr 1fr 2fr 1fr;
  gap: 1rem;
  padding: 1rem;
  align-items: center;
  border-bottom: 1px solid #f3f4f6;
  transition: background-color 0.2s ease;
}

.license-row:last-child {
  border-bottom: none;
}

.license-row:hover {
  background-color: #f9fafb;
}

/* Current license row styling */
.license-row.current-license {
  margin-bottom: 0.5rem;
}

.license-row.current-license:hover {
  background-color: #f0fdf4;
}

/* Column styling */
.license-holder,
.license-edition,
.license-subscription,
.license-renewal,
.license-domain,
.license-action {
  font-family: 'Outfit', sans-serif;
  font-weight: 500;
}

.license-table-header .license-row {
  font-weight: 600;
  color: #374151;
  background-color: #f9fafb;
}

.license-table-header .license-row:hover {
  background-color: #f9fafb;
}

/* Text styling */
.holder-text,
.subscription-text {
  font-family: 'Courier New', monospace;
  font-size: 0.75rem;
  color: #6b7280;
}

/* Loading and no-licenses states */
.loading-row,
.no-licenses-row {
  grid-template-columns: 1fr;
  text-align: center;
  padding: 2rem;
}

.loading-message {
  display: flex;
  align-items: center;
  justify-content: center;
  color: #6b7280;
}

.no-licenses-message {
  width: 100%;
}

.no-licenses-message .vs-alert {
  text-align: left;
}

/* License card header styling */
.license-card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
}

.license-card-title {
  margin: 0;
  font-size: 1.25rem;
  font-weight: 600;
  color: #374151;
}

.refresh-button {
  min-width: auto;
  padding: 0.5rem;
}

/* License info card styling */
.license-info-card {
  background-color: #f0f9ff;
  border: 1px solid #0ea5e9;
}

.license-info-content {
  display: flex;
  align-items: center;
  color: #0c4a6e;
  font-size: 0.875rem;
}

.license-info-content a {
  font-weight: 500;
  text-decoration: none;
}

.license-info-content a:hover {
  text-decoration: underline;
}

/* Success Modal Styling */
.success-modal-content {
  text-align: center;
  padding: 2rem;
}

.success-animation {
  margin-bottom: 2rem;
}

.checkmark-circle {
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background-color: #10b981;
  margin: 0 auto;
  display: flex;
  align-items: center;
  justify-content: center;
  animation: checkmark-pop 0.6s ease-in-out;
}

.checkmark {
  width: 30px;
  height: 30px;
  border-radius: 2px;
  background-color: transparent;
  border-right: 4px solid white;
  border-bottom: 4px solid white;
  transform: rotate(45deg);
  animation: checkmark-draw 0.8s ease-in-out 0.3s both;
}

.success-title {
  color: #10b981;
  font-size: 1.5rem;
  font-weight: 600;
  margin-bottom: 1rem;
}

.success-text {
  color: #6b7280;
  font-size: 1rem;
  line-height: 1.5;
  margin-bottom: 2rem;
}

.success-actions {
  display: flex;
  justify-content: center;
}

/* Animations */
@keyframes checkmark-pop {
  0% {
    transform: scale(0);
    opacity: 0;
  }
  50% {
    transform: scale(1.1);
    opacity: 1;
  }
  100% {
    transform: scale(1);
    opacity: 1;
  }
}

@keyframes checkmark-draw {
  0% {
    width: 0;
    height: 0;
    opacity: 0;
  }
  50% {
    width: 15px;
    height: 15px;
    opacity: 0.5;
  }
  100% {
    width: 30px;
    height: 30px;
    opacity: 1;
  }
}
.edition-text,
.renewal-text,
.domain-text {
  font-family: 'Outfit', sans-serif;
  font-weight: 500;
  color: #374151;
}

.current-license .holder-text,
.current-license .edition-text,
.current-license .renewal-text,
.current-license .domain-text {
  color: #059669;
  font-weight: 600;
}

/* Product cards styling */

/* Email popup styling */
.email-popup-content {
  padding: 1rem;
}

.email-popup-content .flex {
  display: flex;
  gap: 0.5rem;
}

.email-popup-content .justify-end {
  justify-content: flex-end;
}

.email-popup-content .space-x-2 > * + * {
  margin-left: 0.5rem;
}

@media (max-width: 768px) {
  .licensing-page {
    padding: 1rem;
  }
  
  .vx-col {
    padding-left: 0.5rem;
    padding-right: 0.5rem;
  }
  
  /* License status responsive adjustments */
  .license-status-header .flex {
    flex-direction: column;
    align-items: flex-start;
    gap: 1rem;
  }
  
  .license-status-badge {
    align-self: flex-end;
  }
  
  .license-details .vx-row {
    margin: 0;
  }
  
  .license-details .vx-col {
    margin-bottom: 1rem;
  }
}
</style>
